import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { AppBar, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, IconButton, Switch, Toolbar, Typography } from '@mui/material';
import type { PageProps } from '@qwickapps/react-framework';
import { Markdown, Page, usePageContext, usePalette, useTheme } from '@qwickapps/react-framework';
import React, { useRef, useState } from 'react';
import { pdf } from '@react-pdf/renderer';
import { PDFDocument } from '../components/PDFDocument';
import { HeroSection } from '../components/HeroSection';
import { LetterheadFooter } from '../components/LetterheadFooter';
import { LetterheadHeader } from '../components/LetterheadHeader';
import { MarkdownEditor } from '../components/MarkdownEditor';
import { MarkdownPreview } from '../components/MarkdownPreview';
import { PrintView } from '../components/PrintView';
import { TypographySettings } from '../components/TypographySettings';
import { MarkdownCheatsheet } from '../components/MarkdownCheatsheet';
import { useDocument } from '../contexts/DocumentContext';
import { svgToPng } from '../utils/svgToPng';
import { getThemeColors } from '../utils/getThemeColors';
import { isPrintSupported } from '../utils/isPrintSupported';
import qwickIconSvg from '../assets/qwick-icon.svg';

// View component that handles both print and normal modes
const EditorView: React.FC<{ isPrintMode: boolean }> = ({ isPrintMode }) => {
  if (isPrintMode) {
    return <PrintView />;
  }

  return <NormalView />;
};

// Normal view component with interactive elements
const NormalView: React.FC = () => {
  const { content, toggleLetterhead, letterheadConfig, clearDocument, setContent, typographyStyle } = useDocument();
  const { triggerPrint } = usePageContext();
  const { getCurrentTheme, setPreferredTheme } = useTheme();
  const { getCurrentPalette, setPreferredPalette, availablePalettes } = usePalette();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [cheatsheetOpen, setCheatsheetOpen] = useState(false);

  const handlePrint = () => {
    triggerPrint();
    setTimeout(() => {
      window.print();
    }, 150);
  };

  const handleDownloadPDF = async () => {
    if (!content) {
      alert('No content to export');
      return;
    }

    setIsGeneratingPDF(true);
    try {
      // Get theme colors from CSS custom properties
      const themeColors = getThemeColors();

      // Convert SVG logo to PNG if letterhead is enabled
      let logoPng: string | undefined;
      if (letterheadConfig.enabled) {
        try {
          // Use custom logo if provided, otherwise use default QwickIcon
          const logoSrc = letterheadConfig.logoUrl || qwickIconSvg;
          logoPng = await svgToPng(logoSrc, 64, 64);
        } catch (error) {
          console.warn('Could not convert logo to PNG:', error);
          // Continue without logo
        }
      }

      const blob = await pdf(
        <PDFDocument
          content={content}
          typographyStyle={typographyStyle}
          letterheadConfig={letterheadConfig}
          themeColors={themeColors}
          logoPng={logoPng}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'document.pdf';
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleClear = () => {
    setClearDialogOpen(true);
  };

  const handleClearConfirm = () => {
    clearDocument();
    setClearDialogOpen(false);
  };

  const handleClearCancel = () => {
    setClearDialogOpen(false);
  };

  const handleUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (5MB limit)
    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      alert(`File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB`);
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => {
      alert('Error reading file. Please try again.');
    };
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setContent(text);
    };
    reader.readAsText(file);

    // Reset input so same file can be uploaded again
    if (event.target) {
      event.target.value = '';
    }
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".md,.markdown,.txt"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* Hero Section */}
      <HeroSection />

      {/* App Bar */}
      <AppBar position="static" color="transparent" elevation={1}>
        <Toolbar>
          <Box sx={{ flexGrow: 1 }} />

          {/* Download PDF Button */}
          <Button
            variant="contained"
            color="secondary"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadPDF}
            disabled={isGeneratingPDF}
            sx={{ mr: 1 }}
          >
            {isGeneratingPDF ? 'Generating...' : 'Download PDF'}
          </Button>

          {/* Print Button - Only show on desktop where print is reliable */}
          {isPrintSupported() && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
            >
              Print
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {/* Split Pane: Editor + Preview */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Editor Pane */}
        <Box
          sx={{
            width: { xs: '100%', md: '50%' },
            height: { xs: '50%', md: '100%' },
            borderRight: { md: 1 },
            borderBottom: { xs: 1, md: 0 },
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ p: 1, backgroundColor: 'var(--theme-secondary)', borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, minHeight: '48px' }}>
            <Typography variant="subtitle2" fontWeight="medium" color='var(--theme-on-secondary)' sx={{ flexGrow: 1 }}>
              Markdown Editor
            </Typography>

            {/* Markdown Cheatsheet Button */}
            <IconButton onClick={() => setCheatsheetOpen(true)} size="small" title="Markdown Cheatsheet" sx={{ color: 'var(--theme-on-secondary)' }}>
              <MenuBookIcon fontSize="small" />
            </IconButton>

            {/* Open File Button */}
            <IconButton onClick={handleUpload} size="small" title="Upload File" sx={{ color: 'var(--theme-on-secondary)' }}>
              <UploadFileIcon fontSize="small" />
            </IconButton>

            {/* Clear Button */}
            <IconButton onClick={handleClear} size="small" title="Delete Content" sx={{ color: 'var(--theme-on-secondary)' }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <MarkdownEditor />
          </Box>
        </Box>

        {/* Preview Pane */}
        <Box sx={{ width: { xs: '100%', md: '50%' }, height: { xs: '50%', md: '100%' }, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 1, backgroundColor: 'var(--theme-secondary)', borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, minHeight: '48px' }}>
            <Typography variant="subtitle2" fontWeight="medium" color='var(--theme-on-secondary)' sx={{ flexGrow: 1 }}>
              Live Preview
            </Typography>

            {/* Typography Settings */}
            <TypographySettings sx={{ color: 'var(--theme-on-secondary)' }} />

            {/* Letterhead Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={letterheadConfig.enabled}
                  onChange={toggleLetterhead}
                  size="small"
                  sx={{ color: 'var(--theme-on-secondary)' }}
                />
              }
              label="Letterhead"
              sx={{
                m: 0,
                color: 'var(--theme-on-secondary)',
                '& .MuiFormControlLabel-label': {
                  color: 'var(--theme-on-secondary)'
                }
              }}
            />
          </Box>
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <MarkdownPreview />
          </Box>
        </Box>
      </Box>

      {/* Clear Confirmation Dialog */}
      <Dialog open={clearDialogOpen} onClose={handleClearCancel}>
        <DialogTitle>Clear Document?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will delete your current document and reset to the default content. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClearCancel}>Cancel</Button>
          <Button onClick={handleClearConfirm} color="error" variant="contained">
            Clear
          </Button>
        </DialogActions>
      </Dialog>

      {/* Markdown Cheatsheet Sidebar */}
      <MarkdownCheatsheet open={cheatsheetOpen} onClose={() => setCheatsheetOpen(false)} />
    </Box>
  );
};

// Main Page Class
class EditorPage extends Page<PageProps> {
  getPageProps(): PageProps {
    return {
      slug: 'editor',
      title: 'QwickPDF - Create Professional PDFs from Markdown',
      description: 'Transform markdown into professional PDFs with custom letterheads, rich typography, and complete privacy - everything stays in your browser',
      printConfig: {
        showPrintDate: false,
        pageMargins: '0mm',
        printHeader: <LetterheadHeader />,
        printFooter: <LetterheadFooter />,
        printHeaderHeight: '80px',
        printFooterHeight: '50px',
      },
    };
  }

  renderView() {
    return <EditorView isPrintMode={false} />;
  }

  renderPrintView() {
    return <EditorView isPrintMode={true} />;
  }
}

export default EditorPage;
